import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce'; //NOSONAR
import {
  DEFAULT_CALLBACK_DEBOUNCE,
  IDLE_EVENTS,
  LOCAL_STORAGE_IDLE_ACTION_ID,
} from '../features/common/Common.constants';
import { setupLocalStorageIdleActionId } from '../features/common/Common.utils';
import useInterval from './useInterval';

interface UseIdleProps {
  debounce?: number;
  timeout: number;
  onIdle?: () => void;
  onActive?: () => void;
  onAction?: () => void;
}

function useIdle({
  debounce = DEFAULT_CALLBACK_DEBOUNCE,
  timeout,
  onAction,
  onIdle,
  onActive,
}: UseIdleProps): void {
  const [isIdle, setIsIdle] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [intervalDelay, setIntervalDelay] = useState(timeout);
  const [currentActionId, setCurrentActionId] =
    useState<string | undefined>(undefined);

  useEffect(() => {
    if (!isIdle) {
      IDLE_EVENTS.forEach((evt) => {
        window.addEventListener(evt, handleOnActionDebounced);
      });
    }
    return () => {
      IDLE_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, handleOnActionDebounced);
      });
    };
  }, [isIdle]);

  useEffect(() => {
    window.addEventListener('click', handleOnActive);
    return () => {
      window.removeEventListener('click', handleOnActive);
    };
  }, []);

  const handleOnActive = (): void => {
    setIsIdle(false);
    onActive && onActive();
    resetInterval();
    setSetActionId();
  };

  const setSetActionId = (): void => {
    const newActionId = setupLocalStorageIdleActionId();
    setCurrentActionId(newActionId);
  };

  useEffect(() => {
    handleOnActive();
    setIsFirstRender(false);
  }, []);

  const handleOnIdle = (): void => {
    if (!isIdle) {
      setIsIdle(true);
      onIdle && onIdle();
    }
  };

  const handleOnAction = (): void => {
    setIsIdle(false);
    onAction && onAction();
    resetInterval();
  };

  const handleAction = (): void => {
    handleOnAction();
    const newActionId = setupLocalStorageIdleActionId();
    setCurrentActionId(newActionId);
  };

  const resetInterval = (): void => {
    setIntervalDelay(0);
    setIntervalDelay(timeout);
  };

  const handleOnActionDebounced = useDebouncedCallback(handleAction, debounce);

  useEffect(() => {
    if (!isFirstRender) {
      window.addEventListener('storage', checkLocalStorageIdleHandler);
    }
    return () => {
      window.removeEventListener('storage', checkLocalStorageIdleHandler);
    };
  }, [isFirstRender]);

  const checkLocalStorageIdleHandler = (e: StorageEvent): void => {
    if (e.key === LOCAL_STORAGE_IDLE_ACTION_ID) {
      const idleActionId = e.newValue;
      if (idleActionId && idleActionId !== currentActionId) {
        handleOnAction();
        setCurrentActionId(idleActionId);
      }
    }
  };

  useInterval({
    callback: handleOnIdle,
    delay: intervalDelay,
    isEnabled: !isIdle,
  });
}

export default useIdle;
